import { linkTargets, type LinkTarget } from '@worknet/models';

import { getEmbedderInfo, getLinkTarget } from './urlParamsValues';

// Where links rendered by the chat should open, configured by the embedder via
// `window.__WorknetWidget.chat.linkTarget` and forwarded to us as a query param.
// Read once at module load, like the theme colors in `theme.ts`.
const configuredTarget: LinkTarget = resolveSetting(getLinkTarget());

const isEmbedded = detectIsEmbedded();
const hostOrigin = detectHostOrigin();

export type ResolvedLinkTarget = {
  target: LinkTarget;
  rel?: string;
};

export function resolveLinkTarget(href?: string): ResolvedLinkTarget {
  const target = configuredTarget === 'auto' ? resolveAutoTarget(href) : configuredTarget;

  return {
    target,
    ...(target === '_blank' ? { rel: 'noopener noreferrer' } : {}),
  };
}

// For links opened from a click handler rather than an anchor (server link
// actions).
export function openLink(url: string): void {
  const { target } = resolveLinkTarget(url);
  window.open(url, target, target === '_blank' ? 'noopener,noreferrer' : undefined);
}

// Must stay a `window.open` navigation. A citation for an uploaded file points at a
// presigned S3 URL, and a top-level navigation is not a CORS request — which is why the
// uploads bucket only has to allow our own origins. Fetching it and offering a blob
// instead would need every embedder's origin allowed, which is an unbounded set.
export function openCitationSource(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer');
}

function resolveSetting(value?: string): LinkTarget {
  return linkTargets.includes(value as LinkTarget) ? (value as LinkTarget) : '_blank';
}

// `auto`: links back into the embedder's own site take over the page the chat is
// embedded in, everything else opens in a new tab. `_self` would navigate the
// chat iframe itself and replace the chat UI, so it is only used when we are not
// embedded at all.
function resolveAutoTarget(href?: string): LinkTarget {
  if (!href || !hostOrigin) {
    return '_blank';
  }

  try {
    const url = new URL(href, window.location.href);
    // mailto:, tel: and friends are handed to the OS — a new tab keeps the chat
    // in place while the handler opens.
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return '_blank';
    }
    if (url.origin !== hostOrigin) {
      return '_blank';
    }
    return isEmbedded ? '_top' : '_self';
  } catch {
    return '_blank';
  }
}

function detectIsEmbedded(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin ancestor that denies access — we are definitely in a frame.
    return true;
  }
}

// `document.referrer` is the embedder for an iframe; `embedderInfo.currentUrl` is
// the same value collected by the widget, used when the referrer is suppressed.
function detectHostOrigin(): string | undefined {
  const candidates = [
    document.referrer,
    tryGetEmbedderCurrentUrl(),
    isEmbedded ? undefined : window.location.href,
  ];

  for (const candidate of candidates) {
    if (!candidate) {
      continue;
    }
    try {
      return new URL(candidate).origin;
    } catch {
      continue;
    }
  }

  return undefined;
}

function tryGetEmbedderCurrentUrl(): string | undefined {
  try {
    return getEmbedderInfo()?.currentUrl;
  } catch {
    return undefined;
  }
}
